/**
 * Trilingual (TR / EN / DE) copy for the Corporate / About page.
 *
 * Powers the navbar "Corporate" dropdown. Section keys map 1:1 to the dropdown
 * anchors; each section carries an `id` to be used as the in-page `#anchor`
 * target. The anchor `id`s are LOCALISED per the navbar spec (see the
 * section/anchor map in the handover notes), so the dropdown links differ by
 * locale while the data shape stays identical across all three.
 *
 * Sourcing discipline (industrial automotive B2B — precise, no hype):
 *   - Only verified facts are stated: 25+ years, Bursa / Osmangazi location,
 *     three service lines (pad printing / screen printing / assembly & trim),
 *     and ISO 9001:2015 as the SOLE cited certification (as on the home page
 *     and the footer). Ankaplastik does NOT hold IATF 16949 (owner,
 *     2026-10-04): never cite it.
 *   - No invented certifications, client/brand names, awards, founding year,
 *     headcount, facility size, or production figures. Where a hard specific
 *     would normally appear, copy stays deliberately neutral.
 *   - Placeholder-but-credible copy is acceptable; fabricated facts are not.
 *     A few spots carry a `[TODO: doğrula]` token for figures the client must
 *     confirm before launch — never replace these with invented numbers.
 *
 * Parity rule: TR / EN / DE share IDENTICAL keys and shape. Any change to one
 * locale MUST be mirrored in the other two. Distinct registers are intentional
 * (TR professional/warm, EN direct, DE formal with Sie) but the brand is one.
 *
 * Shape per locale:
 *   hero           — { title, lead }
 *   about          — { id, heading, paragraphs[] }
 *   vision         — { id, heading, vision: { h, body }, mission: { h, body } }
 *   values         — { id, heading, items: [{ title, desc }] }
 *   quality        — { id, heading, paragraphs[], pullQuote }
 *   certifications — { id, heading, standard, body, ctaLabel }  (standard: the title-block key, as named in body)
 *   cta            — { heading, body, buttonLabel }
 *   seo            — { title, description }
 *
 * @typedef {Object} CorporateLocale
 * @property {{ title: string, lead: string }} hero
 * @property {{ id: string, heading: string, paragraphs: string[] }} about
 * @property {{ id: string, heading: string, vision: { h: string, body: string }, mission: { h: string, body: string } }} vision
 * @property {{ id: string, heading: string, items: Array<{ title: string, desc: string }> }} values
 * @property {{ id: string, heading: string, paragraphs: string[], pullQuote: string }} quality
 * @property {{ id: string, heading: string, standard: string, body: string, ctaLabel: string }} certifications
 * @property {{ heading: string, body: string, buttonLabel: string }} cta
 * @property {{ title: string, description: string }} seo
 */

/** @type {{ tr: CorporateLocale, en: CorporateLocale, de: CorporateLocale }} */
export const CORPORATE_I18N = {
  tr: {
    hero: {
      title: 'Otomotiv Kalitesinde Üretim, Bursa’dan',
      lead: 'Ankaplastik, 25 yılı aşkın deneyimiyle otomotiv ve endüstriyel sektörlere tampon baskı, serigrafi ve montaj-trim çözümleri sunan bir tedarikçidir.',
    },
    about: {
      id: 'hakkimizda',
      heading: 'Hakkımızda',
      paragraphs: [
        'Bursa Osmangazi’de faaliyet gösteren Ankaplastik, endüstriyel üretim alanında 25 yılı aşkın deneyime sahip bir tedarikçidir. Yıllar içinde odağını otomotiv yan sanayine taşıyarak, global tedarik zincirinin titiz kalite beklentilerini karşılayan bir üretim disiplini geliştirmiştir.',
        'Üç temel hizmet alanında uzmanlaşıyoruz: üç boyutlu yüzeylere hassas baskı sağlayan tampon baskı, geniş yüzeyler ve yüksek hacimler için serigrafi, ve çok bileşenli parçaların montajı ile dekoratif trim uygulamaları. Araç içi trim parçalarından güvenlik bileşenlerine kadar geniş bir ürün yelpazesinde üretim gerçekleştiriyoruz.',
        'Tanımlı ve kayıt altındaki süreçlerimiz, tasarım girdisinden sevkiyata kadar her aşamada izlenebilirlik ve tekrarlanabilirlik sağlar. Amacımız iddialı vaatler değil; her partide aynı kaliteyi veren, öngörülebilir bir üretimdir.',
      ],
    },
    vision: {
      id: 'vizyon',
      heading: 'Vizyon ve Misyon',
      vision: {
        h: 'Vizyonumuz',
        body: 'Otomotiv baskı ve montaj uygulamalarında, müşterilerimizin uzun vadeli güvenini kazanan; teknik yetkinliği, tutarlılığı ve kalite disiplini ile tercih edilen bir çözüm ortağı olmak.',
      },
      mission: {
        h: 'Misyonumuz',
        body: 'Her projeyi kendi teknik gereksinimleri çerçevesinde ele alarak; izlenebilir ve tekrarlanabilir süreçlerle, zamanında ve sapmasız üretim sunmak.',
      },
    },
    values: {
      id: 'degerler',
      heading: 'Değerlerimiz',
      items: [
        {
          title: 'Kalite',
          desc: 'Kaliteyi bir kontrol adımı değil, sürecin tamamına gömülü bir disiplin olarak ele alıyoruz. Kalite yönetim sistemimiz her aşamada referans noktamızdır.',
        },
        {
          title: 'Hassasiyet',
          desc: 'Karmaşık geometriler ve dar toleranslarda doğru sonucu ilk seferde almak için süreçlerimizi sıkı şekilde kontrol ediyoruz.',
        },
        {
          title: 'Güvenilirlik',
          desc: 'Verdiğimiz sözü tutmak; tutarlı kalite, öngörülebilir teslimat ve şeffaf iletişim, çalışma biçimimizin temelidir.',
        },
        {
          title: 'Müşteri Odağı',
          desc: 'Her müşterinin teknik beklentisini anlayarak, standart çözümler yerine projeye özel ve ölçeklenebilir yaklaşımlar geliştiriyoruz.',
        },
      ],
    },
    quality: {
      id: 'kalite',
      heading: 'Kalite Politikamız',
      paragraphs: [
        'Kalite anlayışımız, ISO 9001:2015 kalite yönetim sisteminin gerektirdiği sistematik yaklaşım üzerine kuruludur. Süreç kontrolü, izlenebilirlik ve sürekli iyileştirme; üretimin sonradan denetlenen bir özelliği değil, tasarımdan sevkiyata kadar her adıma yerleşik bir çalışma biçimidir.',
        'Hammadde girişinden nihai kontrole kadar tanımlı kontrol noktaları uyguluyor, sapmaları kök neden düzeyinde ele alıyor ve düzeltici faaliyetleri belgeliyoruz. Bu disiplin, müşterilerimize parti bazında tekrarlanabilir bir kalite sunmamızı sağlar.',
      ],
      pullQuote: 'Kalite, son kontrolde değil; her süreç adımında kazanılır.',
    },
    certifications: {
      id: 'sertifikalar',
      heading: 'Sertifikalar',
      standard: 'ISO 9001:2015',
      body: 'Üretim süreçlerimiz, uluslararası kalite yönetim sistemi standardı olan ISO 9001:2015 sertifikası ile yürütülmektedir. Bu standart, süreç odaklı kalite yönetimini ve sürekli iyileştirmeyi güvence altına alır.',
      ctaLabel: 'Bizimle iletişime geçin',
    },
    cta: {
      heading: 'Projenizi konuşalım',
      body: 'Tampon baskı, serigrafi veya montaj-trim ihtiyaçlarınız için teknik ekibimizle iletişime geçin. Gereksinimlerinizi birlikte değerlendirelim.',
      buttonLabel: 'İletişime Geç',
    },
    seo: {
      title: 'Kurumsal — Bursa Otomotiv Baskı',
      description:
        'Ankaplastik: Bursa Osmangazi’de 25+ yıllık deneyime sahip otomotiv tedarikçisi. Tampon baskı, serigrafi ve montaj-trim çözümleri.',
    },
  },

  en: {
    hero: {
      title: 'Automotive-Grade Manufacturing, from Bursa',
      lead: 'Ankaplastik is a supplier with more than 25 years of experience, providing pad printing, screen printing, and assembly-trim solutions to the automotive and industrial sectors.',
    },
    about: {
      id: 'about',
      heading: 'About Us',
      paragraphs: [
        'Based in Osmangazi, Bursa, Ankaplastik is an industrial supplier with more than 25 years of manufacturing experience. Over the years we have shifted our focus toward the automotive supply industry, building a production discipline that meets the rigorous quality expectations of the global supply chain.',
        'We specialise in three core service lines: pad printing for precise application onto three-dimensional surfaces, screen printing for large surfaces and high volumes, and the assembly of multi-component parts together with decorative trim applications. Our work spans interior trim parts through to safety components.',
        'Our defined, documented processes provide traceability and repeatability at every stage, from design input to dispatch. Our aim is not bold claims but predictable production that delivers the same quality batch after batch.',
      ],
    },
    vision: {
      id: 'vision',
      heading: 'Vision & Mission',
      vision: {
        h: 'Our Vision',
        body: 'To be a solution partner of choice in automotive printing and assembly applications — earning long-term customer trust through technical capability, consistency, and quality discipline.',
      },
      mission: {
        h: 'Our Mission',
        body: 'To treat every project within the frame of its own technical requirements and deliver on-time, deviation-free production through traceable, repeatable processes.',
      },
    },
    values: {
      id: 'values',
      heading: 'Our Values',
      items: [
        {
          title: 'Quality',
          desc: 'We treat quality not as a final inspection step but as a discipline built into the entire process. Our quality management system is our reference point at every stage.',
        },
        {
          title: 'Precision',
          desc: 'We keep our processes under tight control to get the right result the first time on complex geometries and narrow tolerances.',
        },
        {
          title: 'Reliability',
          desc: 'Keeping our word is the foundation of how we work: consistent quality, predictable delivery, and transparent communication.',
        },
        {
          title: 'Customer Focus',
          desc: 'We understand each customer’s technical expectations and develop project-specific, scalable approaches rather than off-the-shelf solutions.',
        },
      ],
    },
    quality: {
      id: 'quality',
      heading: 'Our Quality Policy',
      paragraphs: [
        'Our approach to quality is built on the systematic discipline required by the ISO 9001:2015 quality management system. Process control, traceability, and continuous improvement are not properties checked after the fact — they are embedded in every step, from design to dispatch.',
        'We apply defined control points from incoming raw material through to final inspection, address deviations at root-cause level, and document corrective actions. This discipline lets us deliver quality that is repeatable on a batch-by-batch basis.',
      ],
      pullQuote: 'Quality is earned at every process step — not at final inspection.',
    },
    certifications: {
      id: 'certificates',
      heading: 'Certifications',
      standard: 'ISO 9001:2015',
      body: 'Our production processes are operated under ISO 9001:2015, the international standard for quality management systems. The standard ensures process-focused quality management and continuous improvement.',
      ctaLabel: 'Get in touch',
    },
    cta: {
      heading: 'Let’s discuss your project',
      body: 'Contact our technical team for your pad printing, screen printing, or assembly-trim requirements. Let’s evaluate your specifications together.',
      buttonLabel: 'Contact Us',
    },
    seo: {
      title: 'Corporate — Bursa Automotive Printing',
      description:
        'Ankaplastik: an automotive supplier in Osmangazi, Bursa with 25+ years of experience. Pad printing, screen printing, and assembly-trim solutions.',
    },
  },

  de: {
    hero: {
      title: 'Fertigung in Automobilqualität, aus Bursa',
      lead: 'Ankaplastik ist ein Zulieferer mit über 25 Jahren Erfahrung und bietet Tampondruck, Siebdruck sowie Montage-Trim-Lösungen für die Automobil- und Industriebranche.',
    },
    about: {
      id: 'ueber-uns',
      heading: 'Über uns',
      paragraphs: [
        'Ankaplastik mit Sitz in Osmangazi, Bursa, ist ein Industriezulieferer mit über 25 Jahren Fertigungserfahrung. Im Laufe der Jahre hat das Unternehmen seinen Schwerpunkt auf die Automobilzulieferindustrie verlagert und eine Fertigungsdisziplin aufgebaut, die den anspruchsvollen Qualitätserwartungen der globalen Lieferkette gerecht wird.',
        'Wir sind auf drei zentrale Leistungsbereiche spezialisiert: Tampondruck für die präzise Bedruckung dreidimensionaler Oberflächen, Siebdruck für große Flächen und hohe Stückzahlen sowie die Montage von Mehrkomponententeilen in Verbindung mit dekorativen Trim-Anwendungen. Unser Leistungsspektrum reicht von Innenraum-Trimteilen bis hin zu Sicherheitskomponenten.',
        'Unsere definierten, dokumentierten Prozesse gewährleisten Rückverfolgbarkeit und Wiederholbarkeit in jeder Phase — von der Designeingabe bis zum Versand. Unser Anspruch sind keine vollmundigen Versprechen, sondern eine vorhersehbare Fertigung, die in jeder Charge dieselbe Qualität liefert.',
      ],
    },
    vision: {
      id: 'vision',
      heading: 'Vision & Mission',
      vision: {
        h: 'Unsere Vision',
        body: 'Ein bevorzugter Lösungspartner für Druck- und Montageanwendungen in der Automobilindustrie zu sein — und durch technische Kompetenz, Konstanz und Qualitätsdisziplin das langfristige Vertrauen unserer Kunden zu gewinnen.',
      },
      mission: {
        h: 'Unsere Mission',
        body: 'Jedes Projekt im Rahmen seiner eigenen technischen Anforderungen zu behandeln und durch rückverfolgbare, wiederholbare Prozesse eine termingerechte und abweichungsfreie Fertigung zu liefern.',
      },
    },
    values: {
      id: 'werte',
      heading: 'Unsere Werte',
      items: [
        {
          title: 'Qualität',
          desc: 'Qualität verstehen wir nicht als abschließenden Prüfschritt, sondern als eine in den gesamten Prozess eingebettete Disziplin. Unser Qualitätsmanagementsystem ist in jeder Phase unser Bezugspunkt.',
        },
        {
          title: 'Präzision',
          desc: 'Wir halten unsere Prozesse streng unter Kontrolle, um bei komplexen Geometrien und engen Toleranzen schon beim ersten Mal das richtige Ergebnis zu erzielen.',
        },
        {
          title: 'Zuverlässigkeit',
          desc: 'Wort zu halten ist die Grundlage unserer Arbeitsweise: konstante Qualität, planbare Lieferung und transparente Kommunikation.',
        },
        {
          title: 'Kundenorientierung',
          desc: 'Wir verstehen die technischen Erwartungen jedes Kunden und entwickeln projektspezifische, skalierbare Ansätze statt Standardlösungen von der Stange.',
        },
      ],
    },
    quality: {
      id: 'qualitaet',
      heading: 'Unsere Qualitätspolitik',
      paragraphs: [
        'Unser Qualitätsverständnis beruht auf der systematischen Disziplin, die das Qualitätsmanagementsystem nach ISO 9001:2015 verlangt. Prozesssteuerung, Rückverfolgbarkeit und kontinuierliche Verbesserung sind keine nachträglich geprüften Eigenschaften, sondern in jeden Schritt eingebettet — vom Design bis zum Versand.',
        'Vom Wareneingang des Rohmaterials bis zur Endkontrolle wenden wir definierte Kontrollpunkte an, behandeln Abweichungen auf Ursachenebene und dokumentieren Korrekturmaßnahmen. Diese Disziplin ermöglicht es uns, eine chargenweise wiederholbare Qualität zu liefern.',
      ],
      pullQuote: 'Qualität entsteht in jedem Prozessschritt — nicht erst bei der Endkontrolle.',
    },
    certifications: {
      id: 'zertifikate',
      heading: 'Zertifikate',
      standard: 'ISO 9001:2015',
      body: 'Unsere Fertigungsprozesse werden nach ISO 9001:2015 betrieben, dem internationalen Standard für Qualitätsmanagementsysteme. Der Standard sichert ein prozessorientiertes Qualitätsmanagement und kontinuierliche Verbesserung.',
      ctaLabel: 'Kontakt aufnehmen',
    },
    cta: {
      heading: 'Sprechen wir über Ihr Projekt',
      body: 'Kontaktieren Sie unser technisches Team für Ihre Anforderungen in Tampondruck, Siebdruck oder Montage-Trim. Lassen Sie uns Ihre Spezifikationen gemeinsam bewerten.',
      buttonLabel: 'Kontakt',
    },
    seo: {
      title: 'Unternehmen — Automobildruck Bursa',
      description:
        'Ankaplastik: Automobilzulieferer in Osmangazi, Bursa mit über 25 Jahren Erfahrung. Tampondruck, Siebdruck und Montage-Trim.',
    },
  },
}
